import { Group, Text } from '@mantine/core';
import { ReactElement } from 'react';
import { ChipFilterState } from './useChipFilters';
import { ToggleFilterChip } from './ToggleFilterChip';

/**
 * The chip row for a working queue, with its count.
 *
 * The count is rendered here on purpose. The reason "Inactive hidden" is a
 * visible removable chip rather than a silent default is that the count beside it
 * is only true given that filter — so the two belong to one component, and the
 * caller passes the length of the rows it actually shows. Split apart, they can
 * disagree, and a queue whose total quietly excludes people is how a rep stops
 * trusting the number.
 */
export interface ChipFilterBarProps<T> {
  state: ChipFilterState<T>;
  /** Length of the filtered rows on screen — never the unfiltered total. */
  count: number;
  noun?: string;
}

export function ChipFilterBar<T>({ state, count, noun = 'patient' }: ChipFilterBarProps<T>): ReactElement {
  return (
    <Group gap="xs" wrap="wrap">
      <Text size="sm" c="dimmed">
        {`${count} ${count === 1 ? noun : `${noun}s`}`}
      </Text>
      {state.definitions.map((definition) => (
        <ToggleFilterChip
          key={definition.id}
          label={definition.label}
          active={state.activeIds.includes(definition.id)}
          onToggle={() => state.toggle(definition.id)}
          onRemove={definition.removable ? () => state.remove(definition.id) : undefined}
        />
      ))}
    </Group>
  );
}
